import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "495",
  "slug": "largest-seas-in-the-world-ranked",
  "title": "The Largest Seas in the World, Ranked",
  "excerpt": "Seas are the smaller, partly enclosed cousins of the oceans — but some are enormous. Here are the largest seas on Earth, from the Philippine Sea on down.",
  "date": "2026-06-22",
  "category": "Geography",
  "image": "https://images.unsplash.com/photo-1555979864-7a8f9b4fddf8?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHxzZWElMjBjb2FzdCUyMGFlcmlhbHxlbnwxfDB8fHwxNzgyMTE2MDM5fDA&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "4 min",
  "author": "EarthGuessr Team",
  "tldr": "The largest seas on Earth are partly enclosed arms of the oceans. The Philippine Sea is usually ranked the biggest, followed by the Coral Sea and the South China Sea. Exact figures vary by source, because there is no single agreed definition of where a sea ends and the open ocean begins.",
  "content": [
    {
      "type": "paragraph",
      "text": "We talk about the oceans as if they were neatly separate, but their edges are crowded with seas — partly enclosed bodies of salt water hemmed in by land, islands, or undersea ridges. Some seas are modest. Others are so vast they dwarf entire oceans of the imagination. Ranking them, though, comes with a catch worth knowing about."
    },
    {
      "type": "heading",
      "text": "A Note on the Numbers"
    },
    {
      "type": "paragraph",
      "text": "There is no single official rulebook for what counts as a sea or exactly where one ends. Different organisations draw the boundaries differently, and some include marginal arms of the ocean that others leave out. That means the areas below are approximate and the precise order can shift depending on the source. Treat this as a well-supported guide rather than a fixed leaderboard, and you will not go far wrong."
    },
    {
      "type": "heading",
      "text": "The Largest Seas, Roughly Ranked"
    },
    {
      "type": "paragraph",
      "text": "Working from the biggest down, these are the seas that consistently top the list:"
    },
    {
      "type": "list",
      "items": [
        "Philippine Sea — the giant of the western Pacific, usually ranked the largest sea on Earth at around 5.7 million square kilometres, and home to some of the deepest ocean trenches.",
        "Coral Sea — off the northeast coast of Australia, famous for the Great Barrier Reef, covering roughly 4.7 million square kilometres.",
        "South China Sea — a busy, strategically vital sea of around 3.5 million square kilometres, ringed by Southeast Asian nations.",
        "Weddell Sea — a cold, ice-choked arm of the Southern Ocean off Antarctica, around 2.8 million square kilometres.",
        "Caribbean Sea — the warm, island-studded sea between the Americas, roughly 2.75 million square kilometres.",
        "Mediterranean Sea — the historic sea between Europe, Africa, and Asia, about 2.5 million square kilometres and nearly landlocked.",
        "Tasman Sea — the stretch between Australia and New Zealand, around 2.3 million square kilometres.",
        "Bering Sea — the cold northern sea between Russia and Alaska, roughly 2 million square kilometres.",
        "Bay of Bengal — technically named a bay, but a vast sea-sized body of around 2.2 million square kilometres in the northern Indian Ocean.",
        "Sea of Okhotsk — a frigid sea off the Russian Far East, about 1.6 million square kilometres."
      ]
    },
    {
      "type": "heading",
      "text": "Why Some Seas Are So Big"
    },
    {
      "type": "paragraph",
      "text": "The largest seas tend to sit where the continents leave wide gaps and chains of islands fence off a slice of ocean. The Philippine Sea, for instance, is bounded by island arcs rather than continental coastlines, which lets it sprawl across a huge area while still being considered a distinct sea. The South China Sea and Coral Sea are similar: broad basins partly ringed by islands and shallow shelves."
    },
    {
      "type": "heading",
      "text": "The Sea With No Shores"
    },
    {
      "type": "paragraph",
      "text": "One sea breaks every rule on this list. The Sargasso Sea, in the middle of the North Atlantic, has no land boundaries at all. Instead it is defined by the ocean currents that circle it, trapping floating mats of sargassum seaweed in a calm, clear expanse. It is a reminder that a sea is really just a part of the ocean we have chosen to name — sometimes by its coastline, and sometimes by its character."
    },
    {
      "type": "heading",
      "text": "Deep Seas and Cold Seas"
    },
    {
      "type": "paragraph",
      "text": "Size is not the only way a sea can be extreme. The Philippine Sea hides some of the deepest places on the planet along the trenches at its edges, where the sea floor plunges far below the surrounding ocean. At the other end of the scale, the Weddell Sea off Antarctica is one of the coldest and most ice-bound seas on Earth, clogged for much of the year with floating ice. Two seas can sit side by side on a size ranking and yet be worlds apart in depth, temperature, and character."
    },
    {
      "type": "heading",
      "text": "Seas Versus Oceans"
    },
    {
      "type": "paragraph",
      "text": "So what makes a sea different from an ocean? Mostly size and enclosure. Oceans are the five great basins that hold the bulk of the planet’s salt water. Seas are smaller and usually partly surrounded by land, sitting where ocean meets continent. A few inland salt lakes, like the Caspian Sea, carry the name sea out of tradition despite being entirely landlocked."
    },
    {
      "type": "paragraph",
      "text": "For a map exercise, locate each sea and trace its connection to the surrounding ocean. Note the straits and land boundaries used in the ranking; different definitions can change the measured area."
    }
  ]
};
